import { AppError } from '@repo/error';
import { errorMessage, t } from '@repo/i18n';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { Eye, MailIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { trpc } from '@/providers/trpc';

interface OfficersTableActionsProps {
  officerId: string;
  isPendingInvitation: boolean;
}

export function OfficersTableActions({
  officerId,
  isPendingInvitation,
}: OfficersTableActionsProps) {
  useLocale();

  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId: string }>();

  const { mutate: resendInvitation, isPending: isResending } =
    trpc.organization.resendInvitation.useMutation({
      onSuccess: () => {
        toast.success(t('workspace.invitationResent'), {
          get description() {
            return t('workspace.aNewInvitationEmailHasBeenSentToThe');
          },
        });
      },
      onError: (err) => {
        const error = AppError.parseError(err);
        toast.error(t('workspace.failedToResendInvitation'), {
          description: errorMessage(AppError.parse(error)),
        });
      },
    });

  const handleResend = () => {
    if (!organizationId) {
      return;
    }
    resendInvitation({ memberId: officerId });
  };

  return (
    <div className="flex items-center gap-1">
      {isPendingInvitation && (
        <Button
          disabled={isResending}
          loading={isResending}
          onClick={handleResend}
          size="sm"
          title={t('workspace.resendInvitationEmail')}
          variant="outline"
        >
          <MailIcon className="me-2 h-4 w-4" />
          {t('workspace.resend')}{' '}
        </Button>
      )}
      <Button onClick={() => navigate(`${officerId}`)} size="sm" variant="ghost">
        <Eye className="me-2 h-4 w-4" />
        {t('auth.view')}{' '}
      </Button>
    </div>
  );
}
